import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CompleteWorkOrderDialog({ workOrder, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parts, setParts] = useState([]);
  const [labor, setLabor] = useState(0);
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState({ part: "", cost: "" });

  const addPart = () => {
    if (!draft.part.trim()) return;
    setParts((p) => [...p, { part: draft.part.trim(), cost: Number(draft.cost) || 0 }]);
    setDraft({ part: "", cost: "" });
  };
  const removePart = (i) => setParts((p) => p.filter((_, idx) => idx !== i));
  const partsTotal = parts.reduce((s, p) => s + (Number(p.cost) || 0), 0);
  const total = partsTotal + (Number(labor) || 0);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/operator/work-orders/${workOrder.id}/complete`, {
        parts, labor_cost: Number(labor) || 0, notes,
      });
      toast.success("Work order completed — vehicle back to available");
      setOpen(false); setParts([]); setLabor(0); setNotes(""); setDraft({ part: "", cost: "" });
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid={`complete-wo-${workOrder.id}`} size="sm"
            className="gap-1.5 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
            <CheckCircle2 className="h-3.5 w-3.5" /> Complete
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Close out · {workOrder.vehicle_reg}</DialogTitle>
          <DialogDescription>Record parts and labor used. Completing frees the vehicle back to available.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Parts used</Label>
            <div className="mt-1.5 flex gap-2">
              <Input data-testid="wo-part-name" placeholder="Part name" value={draft.part}
                onChange={(e) => setDraft({ ...draft, part: e.target.value })} />
              <Input data-testid="wo-part-cost" type="number" placeholder="₹" className="w-24" value={draft.cost}
                onChange={(e) => setDraft({ ...draft, cost: e.target.value })} />
              <Button data-testid="wo-add-part" type="button" variant="outline" size="icon" onClick={addPart}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {parts.length > 0 && (
              <div className="mt-2 space-y-1">
                {parts.map((p, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md bg-muted px-2 py-1 text-sm">
                    <span>{p.part}</span>
                    <span className="flex items-center gap-2">₹{p.cost}
                      <button type="button" onClick={() => removePart(i)}>
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5"><Label>Labor cost (₹)</Label>
            <Input data-testid="wo-labor" type="number" value={labor} onChange={(e) => setLabor(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Notes</Label>
            <Input data-testid="wo-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Work performed" /></div>
          <div className="rounded-md bg-muted px-3 py-2 text-sm">Total: <span className="font-semibold">₹{total}</span></div>
        </div>
        <DialogFooter>
          <Button data-testid="wo-complete-submit" onClick={submit} disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Complete &amp; free vehicle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
